import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { api, mediaBlobUrl } from '../api';
import { DOC_STATUS, MEMBER_STATUS, ROLE_LABEL, UNIT_CATEGORY, UNIT_CATEGORY_ORDER, UNIT_KIND, UNIT_STATUS, dateOnly, daysUntil, docAlertCounts, expiryBadge, fold, platesText, timeAgo, unitCategoryOf, unitName } from '../format';
import type { DocSubjectType, DocumentInput, Session, Unit, UnitDocument, UnitDocumentAttachment } from '../types';
import { Badge, Btn, Card, Empty, Field, Icon, Modal, Notice, Select, Stat, TextArea } from '../components/ui';
import type { IconName } from '../components/ui';
import { usePortal } from '../App';
import { useDensity } from '../useDensity';

// Documentos con vencimiento de la empresa: cola de verificación (lo que suben
// los choferes entra 'pendingReview'), avisos de vencimiento y la vista por
// sujeto (empresa, unidad u operador). También el modal por miembro con su
// unidad habitual (setMemberUnits) — todo es vista de oficina.

// Un <label> solo puede nombrar UN control; el encabezado de un grupo va en
// un <span> con el aire de `.field > label` y el contenedor lo anuncia con
// role="group" + aria-labelledby.
const FIELD_CAPTION: CSSProperties = { fontSize: 13.5, fontWeight: 600 };

/** Catálogo sugerido por jurisdicción y sujeto (spec del módulo): unidades de
    cruce llevan documentos de ambos países; la empresa como sujeto lleva los
    que amparan la operación entera (permiso SCT, póliza de flota…); `type` es
    texto libre en el contrato, por eso «otro» queda disponible. */
export const DOC_CATALOG: { type: string; label: string; country: 'MX' | 'US' | null; subjectType: DocSubjectType }[] = [
  // Unidad
  { type: 'seguroMX', label: 'Seguro (México)', country: 'MX', subjectType: 'unit' },
  { type: 'tarjetaCirculacion', label: 'Tarjeta de circulación', country: 'MX', subjectType: 'unit' },
  { type: 'fisicoMecanica', label: 'Verificación físico-mecánica', country: 'MX', subjectType: 'unit' },
  { type: 'verifAmbiental', label: 'Verificación ambiental', country: 'MX', subjectType: 'unit' },
  { type: 'caat', label: 'CAAT (autotransporte)', country: 'MX', subjectType: 'unit' },
  { type: 'fianza', label: 'Fianza', country: 'MX', subjectType: 'unit' },
  { type: 'seguroUS', label: 'Seguro (EE. UU.)', country: 'US', subjectType: 'unit' },
  { type: 'usRegistration', label: 'Registro (EE. UU.)', country: 'US', subjectType: 'unit' },
  { type: 'dotAnnual', label: 'Inspección anual DOT', country: 'US', subjectType: 'unit' },
  { type: 'authorityMC', label: 'Permiso DOT / MC authority', country: 'US', subjectType: 'unit' },
  { type: 'scac', label: 'SCAC', country: 'US', subjectType: 'unit' },
  // Operador
  { type: 'visaB1', label: 'Visa B1', country: 'US', subjectType: 'member' },
  { type: 'i94', label: 'Permiso I-94', country: 'US', subjectType: 'member' },
  { type: 'pasaporte', label: 'Pasaporte', country: 'MX', subjectType: 'member' },
  { type: 'fast', label: 'Tarjeta FAST', country: 'US', subjectType: 'member' },
  { type: 'medicalUS', label: 'Cartilla médica FMCSA', country: 'US', subjectType: 'member' },
  { type: 'licencia', label: 'Licencia de conducir', country: 'MX', subjectType: 'member' },
  { type: 'aptoMedico', label: 'Apto médico', country: 'MX', subjectType: 'member' },
  // Empresa
  { type: 'constanciaFiscal', label: 'Constancia de situación fiscal', country: 'MX', subjectType: 'company' },
  { type: 'permisoSCT', label: 'Permiso SCT (empresa)', country: 'MX', subjectType: 'company' },
  { type: 'seguroMercancia', label: 'Seguro de mercancía', country: null, subjectType: 'company' },
  { type: 'actaConstitutiva', label: 'Acta constitutiva', country: null, subjectType: 'company' },
  { type: 'polizaFlota', label: 'Póliza de flota', country: null, subjectType: 'company' },
];

/** Etiqueta visible del documento: la suya propia, la del catálogo o el tipo
    crudo. El servidor rellena label=type cuando viene vacío — se trata como
    ausente para que gane la etiqueta del catálogo. */
export function docLabel(doc: UnitDocument): string {
  const own = doc.label && doc.label !== doc.type ? doc.label : '';
  return own || DOC_CATALOG.find(t => t.type === doc.type)?.label || doc.type;
}

/** Título con país: omite el sufijo «(MX)» si la etiqueta ya lo indica
    («Seguro (México)», «Registro (EE. UU.)»). */
function docTitle(doc: UnitDocument): string {
  const label = docLabel(doc);
  return label + (doc.country && !label.endsWith(')') ? ` (${doc.country})` : '');
}

/** Orden por urgencia de vencimiento: los que vencen antes (o ya vencieron) primero; sin fecha al final. */
const byExpiry = (a: UnitDocument, b: UnitDocument) =>
  (daysUntil(a.expiresAt) ?? Number.MAX_SAFE_INTEGER) - (daysUntil(b.expiresAt) ?? Number.MAX_SAFE_INTEGER);

/** Foto del documento: media-ref servida con Bearer — se abre como blob en
    pestaña nueva. El servicio guarda `photoId` pelado (ph-…): el ref completo
    es `company/<cid>/doc/<docId>/<photoId>` (readPhoto autoriza por membresía
    a ESA empresa). Si el campo ya llega con slashes se usa tal cual. */
export function DocPhotoLink({ doc }: { doc: UnitDocument }) {
  const { token, data, authFail } = usePortal();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  if (!doc.photoId) return null;
  const cid = data?.company?.id;
  const ref = doc.photoId.includes('/') ? doc.photoId : cid ? `company/${cid}/doc/${doc.id}/${doc.photoId}` : '';
  if (!ref) return null;
  const open = async () => {
    setBusy(true); setFailed(false);
    try { window.open(await mediaBlobUrl(ref, token), '_blank', 'noopener'); }
    catch (e) { if (authFail(e)) return; setFailed(true); }
    finally { setBusy(false); }
  };
  return <>
    <Btn small tone="ghost" busy={busy} onClick={() => void open()}>Ver foto</Btn>
    {failed && <span className="tiny u-bad">No se pudo abrir la foto.</span>}
  </>;
}

/** Adjuntos del documento (attachments[]): miniaturas para imágenes y botón
    «Ver PDF» para el resto — todo se abre como blob en pestaña nueva, igual
    que DocPhotoLink (media ref company/<cid>/doc/<docId>/<attachmentId>). */
export function DocAttachments({ doc }: { doc: UnitDocument }) {
  const atts = doc.attachments ?? [];
  if (!atts.length) return null;
  return <div className="doc-atts">
    {atts.map(a => <DocAttachment key={a.id} doc={doc} att={a} />)}
  </div>;
}

function DocAttachment({ doc, att }: { doc: UnitDocument; att: UnitDocumentAttachment }) {
  const { data } = usePortal();
  const cid = data?.company?.id ?? '';
  const ref = att.path || (cid ? `company/${cid}/doc/${doc.id}/${att.id}` : '');
  return <AttLink att={att} mediaRef={ref} />;
}

/** Adjunto de un documento de viaje (media kind 'tripdoc'): mismo render que
    el de documento de unidad/operador, con refs `company/<cid>/tripdoc/<docId>/<attId>`. */
export function TripDocAttachment({ docId, att }: { docId: string; att: UnitDocumentAttachment }) {
  const { data } = usePortal();
  const cid = data?.company?.id ?? '';
  const ref = att.path || (cid ? `company/${cid}/tripdoc/${docId}/${att.id}` : '');
  return <AttLink att={att} mediaRef={ref} />;
}

/** Un adjunto por media-ref con Bearer: miniatura si es imagen (carga perezosa)
    o botón «Ver PDF» — se abre como blob en pestaña nueva, como DocPhotoLink. */
function AttLink({ att, mediaRef: ref }: { att: UnitDocumentAttachment; mediaRef: string }) {
  const { token, authFail } = usePortal();
  const isImage = att.contentType.startsWith('image/');
  const [thumb, setThumb] = useState('');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  // La miniatura de la imagen carga perezosa como la foto de unidad/reporte.
  useEffect(() => {
    if (!isImage || !ref) return;
    let alive = true;
    // Token muerto → authFail saca a login; cualquier otro fallo deja la
    // miniatura vacía sin ruido, como siempre.
    void mediaBlobUrl(ref, token).then(u => { if (alive) setThumb(u); }).catch(e => { authFail(e); });
    return () => { alive = false; };
  }, [isImage, ref, token, authFail]);

  const open = async () => {
    if (!ref) return;
    setBusy(true); setFailed(false);
    try { window.open(thumb || await mediaBlobUrl(ref, token), '_blank', 'noopener'); }
    catch (e) { if (authFail(e)) return; setFailed(true); }
    finally { setBusy(false); }
  };

  if (!ref) return null;
  if (isImage) return <button type="button" className="att-thumb" title={att.name || 'Adjunto'} aria-label={`Abrir ${att.name || 'adjunto'}`} disabled={busy} onClick={() => void open()}>
    <span className="att-img">{thumb ? <img src={thumb} alt="" /> : <span className="att-load" />}</span>
    {failed && <span className="tiny u-bad">Error</span>}
  </button>;
  return <span className="hstack">
    <Btn small tone="ghost" busy={busy} onClick={() => void open()}><Icon name="doc" size={14} />{att.name || 'Ver PDF'}</Btn>
    {failed && <span className="tiny u-bad">No se pudo abrir.</span>}
  </span>;
}

/** Badges de un documento: estado de verificación + aviso de vencimiento. */
export function DocBadges({ doc }: { doc: UnitDocument }) {
  const st = DOC_STATUS[doc.status] ?? DOC_STATUS.pendingReview;
  const exp = expiryBadge(doc.expiresAt);
  return <>
    <Badge tone={st.tone}>{st.label}</Badge>
    {exp && <Badge tone={exp.tone}>{exp.label}</Badge>}
  </>;
}

/** Lista de documentos de UN sujeto (unidad u operador): vigentes arriba y el
    historial de versiones reemplazadas (supersededBy) plegado al final.
    Acciones de oficina: verificar/rechazar pendientes, renovar vigentes, editar. */
export function DocList({ docs }: { docs: UnitDocument[] }) {
  const { token, busy, mutate } = usePortal();
  const [renewing, setRenewing] = useState<UnitDocument | null>(null);
  const [rejecting, setRejecting] = useState<UnitDocument | null>(null);
  const [editing, setEditing] = useState<UnitDocument | null>(null);

  const current = docs.filter(d => !d.supersededBy).sort(byExpiry);
  const history = docs.filter(d => !!d.supersededBy)
    .sort((a, b) => (b.supersededAt ?? '').localeCompare(a.supersededAt ?? ''));

  const verify = (doc: UnitDocument) => {
    if (!confirm(`¿Verificar «${docLabel(doc)}»? Quedará como documento vigente.`)) return;
    void mutate(() => api('reviewDocument', { docId: doc.id, decision: 'verify' }, token), 'Documento verificado.');
  };

  return <>
    {!current.length && <Empty icon="doc" title="Sin documentos" message="Agrega el primero con el botón correspondiente." />}
    <div className="list">
      {current.map(doc => <div className="item" key={doc.id}>
        <div className="row between multiline">
          <div>
            <div className="title">{docTitle(doc)}</div>
            <div className="small muted">
              {doc.number ? `Núm. ${doc.number} · ` : ''}
              {doc.expiresAt ? `Vence ${dateOnly(doc.expiresAt)}` : 'Sin vencimiento'}
              {doc.issuedAt ? ` · emitido ${dateOnly(doc.issuedAt)}` : ''}
            </div>
            <div className="tiny muted">Lo subió {doc.uploadedByName || 'un miembro'} {timeAgo(doc.uploadedAt)}</div>
            {doc.status === 'rejected' && doc.rejectNote && <div className="small u-bad">Motivo del rechazo: {doc.rejectNote}</div>}
            {doc.notes && <div className="small muted">{doc.notes}</div>}
            <DocAttachments doc={doc} />
          </div>
          <div className="actions"><DocBadges doc={doc} /></div>
        </div>
        <div className="actions">
          {doc.photoId && <DocPhotoLink doc={doc} />}
          {doc.status === 'pendingReview' && <>
            <Btn small busy={busy} onClick={() => verify(doc)}>Verificar</Btn>
            <Btn small tone="danger" busy={busy} onClick={() => setRejecting(doc)}>Rechazar</Btn>
          </>}
          {doc.status === 'verified' && <Btn small tone="secondary" busy={busy} onClick={() => setRenewing(doc)}>Renovar</Btn>}
          <Btn small tone="ghost" busy={busy} onClick={() => setEditing(doc)}>Editar</Btn>
        </div>
      </div>)}
    </div>

    {history.length > 0 && <details className="collapsible">
      <summary className="small muted">Historial · {history.length} versión{history.length === 1 ? '' : 'es'} reemplazada{history.length === 1 ? '' : 's'}</summary>
      <div className="list">
        {history.map(doc => <div className="item" key={doc.id}>
          <div className="row between multiline">
            <div>
              <div className="title">{docTitle(doc)}</div>
              <div className="small muted">
                {doc.number ? `Núm. ${doc.number} · ` : ''}vencía {dateOnly(doc.expiresAt)} · reemplazado {dateOnly(doc.supersededAt)}
              </div>
              <DocAttachments doc={doc} />
            </div>
            <div className="actions">
              <Badge tone="muted">Historial</Badge>
              {doc.photoId && <DocPhotoLink doc={doc} />}
            </div>
          </div>
        </div>)}
      </div>
    </details>}

    {renewing && <RenewDocModal doc={renewing} onClose={() => setRenewing(null)} />}
    {rejecting && <RejectDocModal doc={rejecting} onClose={() => setRejecting(null)} />}
    {editing && <DocumentForm subjectType={editing.subjectType} subjectId={editing.subjectId} doc={editing} onClose={() => setEditing(null)} />}
  </>;
}

/** Estado de adjuntos del formulario de documento (alta, edición y
    renovación): los ya guardados se conservan salvo ✕ (dropSaved) y los nuevos
    se acumulan — máx 4 entre ambos. `payload()` construye `files` completo del
    input (el servicio REEMPLAZA la lista cuando viaja): conservados,
    descargados por su media-ref y re-encodeados, más los nuevos pendientes. */
export function useDocFiles(doc?: UnitDocument) {
  const { token, data } = usePortal();
  // Adjuntos pendientes: {file, url} — la url es object URL para la miniatura
  // de imágenes y se revoca al quitar/desmontar.
  const [files, setFiles] = useState<{ file: File; url: string }[]>([]);
  const filesRef = useRef(files);
  useEffect(() => { filesRef.current = files; }, [files]);
  useEffect(() => () => { for (const f of filesRef.current) if (f.url) URL.revokeObjectURL(f.url); }, []);
  const [error, setError] = useState('');
  const existing = doc?.attachments ?? [];
  const [dropped, setDropped] = useState<string[]>([]);
  const kept = existing.filter(a => !dropped.includes(a.id));
  const slots = Math.max(0, 4 - kept.length - files.length);
  const changed = dropped.length > 0 || files.length > 0;

  const add = (list: FileList | null) => {
    if (!list?.length) return;
    setError('');
    const next: { file: File; url: string }[] = [];
    for (const f of Array.from(list)) {
      const isImage = f.type.startsWith('image/');
      const isPdf = f.type === 'application/pdf';
      if (!isImage && !isPdf) { setError('Solo fotos o PDF.'); continue; }
      // El PDF no se puede comprimir — se valida su tamaño directo; las fotos
      // grandes las reduce fileToAttachment al guardar.
      if (isPdf && f.size > 4_500_000) { setError(`«${f.name}» supera el máximo de 4.5 MB.`); continue; }
      next.push({ file: f, url: isImage ? URL.createObjectURL(f) : '' });
    }
    const room = Math.max(0, 4 - kept.length - files.length);
    if (next.length > room) setError('Máximo 4 adjuntos por documento.');
    for (const extra of next.slice(room)) if (extra.url) URL.revokeObjectURL(extra.url);
    setFiles(prev => [...prev, ...next.slice(0, room)]);
  };
  const remove = (i: number) => setFiles(prev => {
    const victim = prev[i];
    if (victim?.url) URL.revokeObjectURL(victim.url);
    return prev.filter((_, j) => j !== i);
  });
  const dropSaved = (id: string) => setDropped(prev => [...prev, id]);

  /** `files` del payload completo: conservados (blob→base64 vía media-ref) + nuevos. */
  const payload = async (): Promise<{ name: string; contentType: string; dataBase64: string }[]> => {
    const cid = data?.company?.id ?? '';
    const carried = await Promise.all(kept.map(async a => {
      const ref = a.path || `company/${cid}/doc/${doc!.id}/${a.id}`;
      const url = await mediaBlobUrl(ref, token);
      try { return { name: a.name, contentType: a.contentType, dataBase64: await fileToBase64(await (await fetch(url)).blob()) }; }
      finally { URL.revokeObjectURL(url); }
    }));
    return [...carried, ...await Promise.all(files.map(f => fileToAttachment(f.file)))];
  };
  return { doc, files, error, dropped, kept, slots, changed, add, remove, dropSaved, payload };
}

/** Bloque «Adjuntos» del formulario de documento: guardados con ✕ para
    quitarlos, pendientes con su miniatura/nombre y el chooser (huecos libres). */
export function DocFilesField({ st, busy }: { st: ReturnType<typeof useDocFiles>; busy: boolean }) {
  const captionId = useId();
  return <div className="field" role="group" aria-labelledby={captionId}>
    <span id={captionId} style={FIELD_CAPTION}>Adjuntos (foto o PDF)</span>
    {(st.kept.length > 0 || st.files.length > 0) && <div className="doc-atts">
      {st.kept.map(a => <span key={a.id} className="att-pend">
        <DocAttachment doc={st.doc!} att={a} />
        <button type="button" className="att-x" aria-label={`Quitar ${a.name || 'adjunto'}`} disabled={busy}
          onClick={() => st.dropSaved(a.id)}>✕</button>
      </span>)}
      {st.files.map((f, i) => <span key={i} className="att-pend">
        <span className="att-img">{f.url ? <img src={f.url} alt="" /> : <span className="att-pdf"><Icon name="doc" size={18} />PDF</span>}</span>
        <span className="att-name" title={f.file.name}>{f.file.name}</span>
        <button type="button" className="att-x" aria-label={`Quitar ${f.file.name}`} disabled={busy} onClick={() => st.remove(i)}>✕</button>
      </span>)}
    </div>}
    {st.slots > 0 && <input className="input" type="file" multiple accept="image/*,application/pdf" disabled={busy}
      onChange={e => { st.add(e.target.files); e.target.value = ''; }} />}
    <span className="hint">{[st.dropped.length ? `${st.dropped.length} guardado${st.dropped.length === 1 ? '' : 's'} se quitará${st.dropped.length === 1 ? '' : 'n'} al guardar.` : '',
      `Hasta 4 adjuntos${st.kept.length + st.files.length ? ` (${st.slots} libre${st.slots === 1 ? '' : 's'})` : ''}: fotos o PDF de máximo 4.5 MB.`].filter(Boolean).join(' ')}</span>
    {st.error && <span className="tiny u-bad">{st.error}</span>}
  </div>;
}

/** Alta o edición de un documento (saveDocument). La oficina lo crea ya
    'verified' según el contrato; el tipo se fija al crear (editar no lo cambia). */
export function DocumentForm({ subjectType, subjectId, doc, onClose }: {
  subjectType: DocSubjectType; subjectId: string; doc?: UnitDocument; onClose(): void;
}) {
  const { token, busy, error, mutate, dismiss, authFail, fail } = usePortal();
  const catalog = DOC_CATALOG.filter(t => t.subjectType === subjectType);
  const [type, setType] = useState(doc?.type ?? '');
  const [label, setLabel] = useState(doc?.label ?? '');
  const [country, setCountry] = useState<'MX' | 'US' | ''>(doc?.country ?? '');
  const [number, setNumber] = useState(doc?.number ?? '');
  const [issuedAt, setIssuedAt] = useState(isoToDateInput(doc?.issuedAt));
  const [expiresAt, setExpiresAt] = useState(isoToDateInput(doc?.expiresAt));
  const [notes, setNotes] = useState(doc?.notes ?? '');
  // El prep de adjuntos/foto va ANTES del mutate: sin bandera propia un doble
  // clic arrancaba el trabajo dos veces con `busy` aún apagado.
  const [submitting, setSubmitting] = useState(false);
  const typeOutId = useId();
  const atts = useDocFiles(doc);

  const pickType = (value: string) => {
    setType(value);
    const info = catalog.find(t => t.type === value);
    // Autollenado de etiqueta y jurisdicción; el usuario puede ajustarlos.
    if (info) { setLabel(info.label); setCountry(info.country ?? ''); }
    else if (value === 'otro') setLabel('');
  };

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      // Solo se manda `files` cuando el conjunto cambió: conservados (descargados
      // como blob y re-encodeados) + nuevos pendientes.
      const filesPayload = atts.changed ? await atts.payload() : undefined;
      const input: DocumentInput = {
        subjectType, subjectId, type,
        country: country || null,
        label: label.trim(),
        number: number.trim(),
        issuedAt: dateInputToIso(issuedAt),
        expiresAt: dateInputToIso(expiresAt),
        notes: notes.trim(),
        ...(filesPayload ? { files: filesPayload } : {}),
      };
      // 60 s cuando lleva adjuntos embebidos — mismo criterio que la foto antes.
      const ok = await mutate(() => api('saveDocument', { input, ...(doc ? { docId: doc.id } : {}) }, token, filesPayload ? 60000 : 15000),
        doc ? 'Documento actualizado.' : 'Documento guardado.');
      if (ok) onClose();
    } catch (e) {
      // Un token muerto durante el prep (media-ref → Bearer) también es
      // logout; cualquier otro fallo del prep se reporta al banner — sin esto
      // moría como unhandled rejection sin aviso.
      if (!authFail(e)) fail(e);
    } finally { setSubmitting(false); }
  };

  // Borrado definitivo del documento (doc + binarios; el servidor respeta
  // rutas compartidas con otras versiones de la cadena).
  const remove = async () => {
    if (!doc) return;
    if (!confirm(`¿Eliminar «${docLabel(doc)}»? Se borran también sus archivos adjuntos.`)) return;
    const ok = await mutate(() => api('deleteDocument', { docId: doc.id }, token), 'Documento eliminado.');
    if (ok) onClose();
  };

  const valid = type !== '' && label.trim().length > 0;

  return <Modal title={doc ? `Editar ${docLabel(doc)}` : 'Agregar documento'} onClose={onClose}>
    {error && <Notice tone="danger" onClose={dismiss}>{error}</Notice>}
    <div className="form-grid">
      {doc
        /* El tipo se fija al crear: valor de solo lectura — <output> sí es
           etiquetable, así el <label> conserva a qué apunta. */
        ? <div className="field"><label htmlFor={typeOutId}>Tipo</label><output id={typeOutId} className="name">{docLabel(doc)}</output></div>
        : <Select label="Tipo de documento" value={type} onChange={e => pickType(e.target.value)} autoFocus>
            <option value="">Elige el tipo…</option>
            {catalog.map(t => <option key={t.type} value={t.type}>{t.label}{t.country ? ` (${t.country})` : ''}</option>)}
            <option value="otro">Otro…</option>
          </Select>}
      <Field label="Nombre visible" value={label} onChange={e => setLabel(e.target.value)} maxLength={80}
        placeholder="Ejemplo: Seguro (México)" hint="Con «Otro» escribe el nombre del documento." />
      <Select label="Jurisdicción" value={country} onChange={e => setCountry(e.target.value as 'MX' | 'US' | '')}>
        <option value="">Sin país</option>
        <option value="MX">México (MX)</option>
        <option value="US">Estados Unidos (US)</option>
      </Select>
      <Field label="Número o folio" value={number} onChange={e => setNumber(e.target.value)} maxLength={60} placeholder="Opcional" />
      <Field label="Emitido" type="date" value={issuedAt} onChange={e => setIssuedAt(e.target.value)} />
      <Field label="Vence" type="date" value={expiresAt} onChange={e => setExpiresAt(e.target.value)}
        hint="Los avisos se prenden a 30, 15 y 7 días del vencimiento." />
    </div>
    <TextArea label="Notas" value={notes} onChange={e => setNotes(e.target.value)} maxLength={500} placeholder="Opcional" />
    <DocFilesField st={atts} busy={busy} />
    <div className="actions end modal-actions">
      {doc && <Btn tone="danger" busy={busy} style={{ marginRight: 'auto' }} onClick={() => void remove()}>Eliminar</Btn>}
      <Btn tone="ghost" onClick={onClose}>Cancelar</Btn>
      <Btn busy={busy} disabled={submitting || !valid} onClick={() => void submit()}>{doc ? 'Guardar cambios' : 'Guardar documento'}</Btn>
    </div>
  </Modal>;
}

/** Renovar un documento (renewDocument): versiona — el anterior queda en el
    historial vía supersededBy. Fechas nuevas, número, foto y adjuntos
    opcionales: `files` siempre viaja con la lista resultante — los conservados
    pasan a la versión nueva y vaciarla la deja sin adjuntos (el heredado
    cuenta como reemplazo explícito). */
function RenewDocModal({ doc, onClose }: { doc: UnitDocument; onClose(): void }) {
  const { token, busy, error, mutate, dismiss, authFail, fail } = usePortal();
  const [issuedAt, setIssuedAt] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [number, setNumber] = useState(doc.number);
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoError, setPhotoError] = useState('');
  // El prep de foto/adjuntos va ANTES del mutate: sin bandera propia un doble
  // clic arrancaba el trabajo dos veces con `busy` aún apagado.
  const [submitting, setSubmitting] = useState(false);
  const photoId = useId();
  const atts = useDocFiles(doc);

  const submit = async () => {
    if (submitting) return;
    if (photo && photo.size > 4_500_000) { setPhotoError('La foto supera el tamaño permitido (4.5 MB).'); return; }
    setPhotoError('');
    setSubmitting(true);
    try {
      const photoPayload = photo ? await photoFromFile(photo) : undefined;
      // El servicio espera el patch PLANO junto al docId (whitelist:
      // ['docId','issuedAt','expiresAt','number','photo','files']) — igual que
      // updateTrip. `files` viaja siempre: es la lista completa del doc NUEVO.
      const filesPayload = await atts.payload();
      const payload: Record<string, unknown> = { docId: doc.id, files: filesPayload };
      if (issuedAt) payload.issuedAt = dateInputToIso(issuedAt);
      if (expiresAt) payload.expiresAt = dateInputToIso(expiresAt);
      if (number.trim() !== doc.number) payload.number = number.trim();
      if (photoPayload) payload.photo = photoPayload;
      const ok = await mutate(() => api('renewDocument', payload, token,
        photoPayload || filesPayload.length ? 60000 : 15000), 'Documento renovado — la versión anterior queda en el historial.');
      if (ok) onClose();
    } catch (e) {
      // Token muerto durante el prep → authFail saca a login; otro fallo del
      // prep se reporta al banner en vez de morir como unhandled rejection.
      if (!authFail(e)) fail(e);
    } finally { setSubmitting(false); }
  };

  return <Modal title={`Renovar ${docLabel(doc)}`} onClose={onClose}>
    <Notice tone="info">Crea la versión nueva y archiva la actual en el historial.{doc.expiresAt ? ` Vence hoy: ${dateOnly(doc.expiresAt)}.` : ''}</Notice>
    {error && <Notice tone="danger" onClose={dismiss}>{error}</Notice>}
    <div className="form-grid">
      <Field label="Emitido" type="date" value={issuedAt} onChange={e => setIssuedAt(e.target.value)} />
      <Field label="Vence" type="date" value={expiresAt} onChange={e => setExpiresAt(e.target.value)} autoFocus />
      <Field label="Número o folio" value={number} onChange={e => setNumber(e.target.value)} maxLength={60} />
    </div>
    <div className="field">
      <label htmlFor={photoId}>Foto o escaneo nuevo (opcional)</label>
      <input className="input" id={photoId} type="file" accept="image/jpeg,image/png,image/webp,image/heic,.heic"
        onChange={e => { setPhoto(e.target.files?.[0] ?? null); setPhotoError(''); e.target.value = ''; }} />
      {photoError && <span className="tiny u-bad">{photoError}</span>}
    </div>
    <DocFilesField st={atts} busy={busy} />
    <div className="actions end modal-actions">
      <Btn tone="ghost" onClick={onClose}>Cancelar</Btn>
      {/* El folio también habilita: editarlo solo ya viaja en el payload. */}
      <Btn busy={busy} disabled={submitting || (!expiresAt && !issuedAt && !photo && !atts.changed && number.trim() === doc.number)} onClick={() => void submit()}>Renovar documento</Btn>
    </div>
  </Modal>;
}

/** Rechazo con nota opcional (reviewDocument 'reject'): el motivo lo ve quien lo subió. */
function RejectDocModal({ doc, onClose }: { doc: UnitDocument; onClose(): void }) {
  const { token, busy, mutate } = usePortal();
  const [note, setNote] = useState('');
  return <Modal title={`Rechazar ${docLabel(doc)}`} onClose={onClose}>
    <p className="small muted u-m0">Lo subió {doc.uploadedByName || 'un miembro'} {timeAgo(doc.uploadedAt)}. El rechazo no lo borra: queda marcado y sin validez hasta que se suba de nuevo.</p>
    <TextArea label="Motivo del rechazo" value={note} onChange={e => setNote(e.target.value)} maxLength={500}
      hint="Recomendado — dile a tu equipo qué corregir (foto borrosa, documento vencido…)." />
    <div className="actions end modal-actions">
      <Btn tone="ghost" onClick={onClose}>Cancelar</Btn>
      <Btn tone="danger" busy={busy} onClick={() => {
        void mutate(() => api('reviewDocument', { docId: doc.id, decision: 'reject', ...(note.trim() ? { note: note.trim() } : {}) }, token), 'Documento rechazado.')
          .then(ok => { if (ok) onClose(); });
      }}>Rechazar documento</Btn>
    </div>
  </Modal>;
}

/** Fila del navegador por sujeto: nombre + descripción, conteo de vigentes
    y el badge del peor estado (rojo vencido/rechazado, ámbar por vencer o
    por verificar, verde al día, «Sin docs» en gris). */
type SubjRow = {
  group: 'company' | 'unit' | 'member'; key: string; icon: IconName;
  title: string; sub: string; haystack: string;
  live: number; label: string; tone: 'ok' | 'warn' | 'bad' | 'muted';
};

/** Tarjeta «Documentos» de la oficina: conteos de la empresa, cola por
    verificar, vencidos/por vencer y el navegador por sujeto (empresa,
    unidad u operador) con buscador. */
export function DocumentsCard({ docs, units, members }: { docs: UnitDocument[]; units: Unit[]; members: Session[] }) {
  const { token, busy, mutate, data } = usePortal();
  const company = data?.company ?? null;
  const [subject, setSubject] = useState('');
  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState(false);
  const [rejecting, setRejecting] = useState<UnitDocument | null>(null);
  const [renewing, setRenewing] = useState<UnitDocument | null>(null);
  // Densidad «Cómoda / Compacta» — misma preferencia que Viajes, Unidades
  // y Equipo (useDensity).
  const { dense, seg: densitySeg } = useDensity();

  const unitById = useMemo(() => new Map(units.map(u => [u.id, u])), [units]);
  const memberById = useMemo(() => new Map(members.map(m => [m.uid, m])), [members]);
  const subjectName = (doc: UnitDocument): string => {
    if (doc.subjectType === 'company') return company?.name || 'Empresa';
    if (doc.subjectType === 'unit') { const u = unitById.get(doc.subjectId); return u ? unitName(u, company) : 'Unidad'; }
    const m = memberById.get(doc.subjectId); return m ? (m.displayName || m.email) : 'Operador';
  };

  const pending = docs.filter(d => d.status === 'pendingReview' && !d.supersededBy).sort((a, b) => a.uploadedAt.localeCompare(b.uploadedAt));
  // Vencidos o dentro de la ventana de aviso; los rechazados no son vigentes.
  const expiring = docs.filter(d => !d.supersededBy && d.status !== 'rejected' && expiryBadge(d.expiresAt)).sort(byExpiry);

  const verify = (doc: UnitDocument) => {
    if (!confirm(`¿Verificar «${docLabel(doc)}» de ${subjectName(doc)}?`)) return;
    void mutate(() => api('reviewDocument', { docId: doc.id, decision: 'verify' }, token), 'Documento verificado.');
  };

  // Cabecera de conteos de la empresa, mismo criterio que el badge del
  // menú: por verificar, vencidos (o a ≤7 días), por vencer (8–30 días) y
  // los verificados sin aviso — los tonos se encienden solo con conteo.
  const counts = docAlertCounts(docs);
  const okCount = docs.filter(d => !d.supersededBy && d.status === 'verified' && !expiryBadge(d.expiresAt)).length;

  // Navegador por sujeto: una fila por empresa, unidad u operador. Los
  // operadores son los miembros de campo — la oficina (y el admin del
  // directorio) no lleva documentos personales en esta cola; quienes aún
  // no tienen docs también aparecen para capturar el primero.
  const subjRows = useMemo<SubjRow[]>(() => {
    const worstOf = (subjectDocs: UnitDocument[]) => {
      const live = subjectDocs.filter(d => !d.supersededBy);
      const c = docAlertCounts(live);
      if (!live.length) return { live: 0, label: 'Sin docs', tone: 'muted' as const };
      if (c.expired) return { live: live.length, label: `${c.expired} vencido${c.expired === 1 ? '' : 's'}`, tone: 'bad' as const };
      if (c.rejected) return { live: live.length, label: `${c.rejected} rechazado${c.rejected === 1 ? '' : 's'}`, tone: 'bad' as const };
      if (c.expiring) return { live: live.length, label: `${c.expiring} por vencer`, tone: 'warn' as const };
      if (c.pending) return { live: live.length, label: `${c.pending} por verificar`, tone: 'warn' as const };
      return { live: live.length, label: 'Al día', tone: 'ok' as const };
    };
    const unitIcon = (u: Unit): IconName => {
      if (UNIT_KIND[u.kind]?.icon === 'truck' || unitCategoryOf(u, company) === 'vehicle') return 'truck';
      return unitCategoryOf(u, company) === 'equipment' ? 'wrench' : 'cube';
    };
    const rows: SubjRow[] = [];
    if (company) rows.push({
      group: 'company', key: `company:${company.id}`, icon: 'building',
      title: company.name || 'Empresa', sub: 'Documentos de la empresa',
      haystack: `${company.name} empresa`,
      ...worstOf(docs.filter(d => d.subjectType === 'company' && d.subjectId === company.id)),
    });
    const sortedUnits = units.slice().sort((a, b) =>
      UNIT_CATEGORY_ORDER.indexOf(unitCategoryOf(a, company)) - UNIT_CATEGORY_ORDER.indexOf(unitCategoryOf(b, company))
      || unitName(a, company).localeCompare(unitName(b, company), 'es'));
    for (const u of sortedUnits) rows.push({
      group: 'unit', key: `unit:${u.id}`, icon: unitIcon(u),
      title: unitName(u, company),
      sub: [platesText(u), u.status !== 'active' ? (UNIT_STATUS[u.status]?.label ?? u.status) : ''].filter(Boolean).join(' · ') || UNIT_CATEGORY[unitCategoryOf(u, company)].label,
      haystack: `${unitName(u, company)} ${platesText(u)} ${u.internalNumber} ${u.brand} ${u.model}`,
      ...worstOf(docs.filter(d => d.subjectType === 'unit' && d.subjectId === u.id)),
    });
    const operators = members
      .filter(m => m.companyRole !== 'office' && m.role !== 'admin')
      .sort((a, b) => (a.displayName || a.email).localeCompare(b.displayName || b.email, 'es'));
    for (const m of operators) rows.push({
      group: 'member', key: `member:${m.uid}`, icon: 'person',
      title: m.displayName || m.email || 'Operador',
      sub: [ROLE_LABEL.driver, m.displayName ? m.email : '', m.status !== 'active' ? (MEMBER_STATUS[m.status]?.label ?? m.status) : ''].filter(Boolean).join(' · '),
      haystack: `${m.displayName} ${m.email}`,
      ...worstOf(docs.filter(d => d.subjectType === 'member' && d.subjectId === m.uid)),
    });
    return rows;
  }, [company, units, members, docs]);

  const q = fold(query.trim());
  const visibleRows = q ? subjRows.filter(r => fold(r.haystack).includes(q)) : subjRows;
  const rowsOf = (g: SubjRow['group']) => visibleRows.filter(r => r.group === g);
  const [selType, selId] = subject.split(':');
  const selRow = subjRows.find(r => r.key === subject);
  const selDocs = selId ? docs.filter(d => d.subjectType === selType && d.subjectId === selId) : [];

  // Fila del navegador: icono, nombre + descripción y a la derecha el
  // conteo de vigentes con el badge del peor estado. Clic alterna la selección.
  const subjBtn = (r: SubjRow) => <button key={r.key} type="button"
    className={`subj${subject === r.key ? ' on' : ''}`} aria-pressed={subject === r.key}
    onClick={() => { setSubject(subject === r.key ? '' : r.key); setAdding(false); }}>
    <span className="subj-ic"><Icon name={r.icon} size={18} /></span>
    <span className="subj-main">
      <span className="subj-title ellipsis">{r.title}</span>
      {r.sub && <span className="subj-sub ellipsis">{r.sub}</span>}
    </span>
    <span className="subj-meta">
      {r.live > 0 && <span className="subj-count">{r.live} doc{r.live === 1 ? '' : 's'}</span>}
      <Badge tone={r.tone}>{r.label}</Badge>
    </span>
  </button>;

  // La densidad aplica a las listas .item/.table de la tarjeta (pendientes,
  // vencimientos y documentos del sujeto) y al navegador por sujeto — las
  // reglas .dense .subj* ya viven en density.css; el Card lleva .dense, así
  // que llegan por ancestro, y .subj-groups además la marca explícita por
  // si el bloque se mueve fuera de la tarjeta.
  const hasLists = subjRows.length > 0 || pending.length > 0 || expiring.length > 0;

  return <Card className={dense ? 'dense' : undefined}>
    {/* Sin CardTitle: el título de la sección («Documentos») ya lo dice. */}
    {/* Tira de conteos de la empresa — se enciende el tono solo cuando hay
        algo que atender (igual que los KPIs del Tablero). */}
    <div className="kpis">
      <Stat icon="doc" n={counts.pending} l="Por verificar" tone={counts.pending ? 'warn' : undefined} hint="Docs que sube el equipo" />
      <Stat icon="alert" n={counts.expired} l="Vencidos" tone={counts.expired ? 'bad' : undefined} hint="Vencieron o faltan ≤7 días" />
      <Stat icon="clock" n={counts.expiring} l="Por vencer" tone={counts.expiring ? 'warn' : undefined} hint="Vencen en 8 a 30 días" />
      <Stat icon="check" n={okCount} l="Al día" tone={okCount ? 'ok' : undefined} hint="Verificados sin aviso" />
    </div>

    {pending.length > 0 && <>
      <h3 className="card-title sm">Por verificar · {pending.length}</h3>
      <div className="list">
        {pending.map(doc => <div className="item" key={doc.id}>
          <div className="row between multiline">
            <div>
              <div className="title">{docTitle(doc)} — {subjectName(doc)}</div>
              <div className="small muted">
                {doc.number ? `Núm. ${doc.number} · ` : ''}
                {doc.expiresAt ? `Vence ${dateOnly(doc.expiresAt)}` : 'Sin vencimiento'}
              </div>
              <div className="tiny muted">Lo subió {doc.uploadedByName || 'un miembro'} {timeAgo(doc.uploadedAt)}</div>
              <DocAttachments doc={doc} />
            </div>
            <div className="actions">
              <Badge tone="warn">Por verificar</Badge>
              {(() => { const b = expiryBadge(doc.expiresAt); return b ? <Badge tone={b.tone}>{b.label}</Badge> : null; })()}
            </div>
          </div>
          <div className="actions">
            {doc.photoId && <DocPhotoLink doc={doc} />}
            <Btn small busy={busy} onClick={() => verify(doc)}>Verificar</Btn>
            <Btn small tone="danger" busy={busy} onClick={() => setRejecting(doc)}>Rechazar</Btn>
          </div>
        </div>)}
      </div>
    </>}

    {expiring.length > 0 && <>
      <h3 className="card-title sm">Vencidos o por vencer · {expiring.length}</h3>
      <div className="table-scroll"><table className="table">
        <thead><tr><th>Documento</th><th>Sujeto</th><th>Vence</th><th>Aviso</th><th /></tr></thead>
        <tbody>
          {expiring.map(doc => {
            const b = expiryBadge(doc.expiresAt);
            return <tr key={doc.id}>
              {/* Primera columna como título (convención de la tabla de
                  unidades): sin data-label, en móvil encabeza la tarjeta. */}
              <td>
                <div className="name">{docTitle(doc)}</div>
                {doc.number && <div className="tiny muted">Núm. {doc.number}</div>}
              </td>
              <td className="small" data-label="Sujeto">{subjectName(doc)}</td>
              <td className="small" data-label="Vence">{dateOnly(doc.expiresAt)}</td>
              <td data-label="Aviso">{b && <Badge tone={b.tone}>{b.label}</Badge>}</td>
              <td className="right">
                {doc.status === 'verified' && <Btn small tone="secondary" busy={busy} onClick={() => setRenewing(doc)}>Renovar</Btn>}
              </td>
            </tr>;
          })}
        </tbody>
      </table></div>
    </>}

    {!pending.length && !expiring.length && <Empty icon="doc" title="Todo al día" message="No hay documentos por verificar ni vencimientos próximos." />}

    {/* Navegador por sujeto: buscador + tres grupos (Empresa, Unidades,
        Operadores); la lista scrollea y cada fila resume su estado. */}
    <Field label="Documentos por sujeto" type="search" value={query} onChange={e => setQuery(e.target.value)}
      placeholder="Buscar por nombre, placas o folio…"
      hint="Toca una fila para ver sus documentos o agregar nuevos." />
    {/* Barra de la lista (misma convención que Viajes/Unidades): densidad a
        la izquierda, conteo de sujetos a la derecha. Solo con listas que
        densificar. */}
    {hasLists && <div className="u-toolbar">
      {densitySeg}
      <span className="tiny muted">{visibleRows.length} {visibleRows.length === 1 ? 'sujeto' : 'sujetos'}</span>
    </div>}
    <div className={`subj-groups${dense ? ' dense' : ''}`}>
      {rowsOf('company').length > 0 && <div className="subj-group">
        <div className="subj-head">Empresa</div>
        {rowsOf('company').map(subjBtn)}
      </div>}
      {rowsOf('unit').length > 0 && <div className="subj-group">
        <div className="subj-head">Unidades{rowsOf('unit').length ? ` · ${rowsOf('unit').length}` : ''}</div>
        {rowsOf('unit').map(subjBtn)}
      </div>}
      {rowsOf('member').length > 0 && <div className="subj-group">
        <div className="subj-head">Operadores{rowsOf('member').length ? ` · ${rowsOf('member').length}` : ''}</div>
        {rowsOf('member').map(subjBtn)}
      </div>}
      {visibleRows.length === 0 && <Empty
        title={q ? 'Sin coincidencias' : 'Sin sujetos'}
        message={q ? `«${query.trim()}» no coincide con el nombre, placas o folio de ningún sujeto.` : 'Las unidades y los operadores de la empresa aparecerán aquí.'} />}
    </div>
    {selId && <>
      <h3 className="card-title sm">Documentos{selRow ? ` · ${selRow.title}` : ''}</h3>
      <DocList docs={selDocs} />
      <div className="actions"><Btn small onClick={() => setAdding(true)}>Agregar documento</Btn></div>
    </>}

    {adding && (selType === 'unit' || selType === 'member' || selType === 'company') && selId &&
      <DocumentForm subjectType={selType} subjectId={selId} onClose={() => setAdding(false)} />}
    {rejecting && <RejectDocModal doc={rejecting} onClose={() => setRejecting(null)} />}
    {renewing && <RenewDocModal doc={renewing} onClose={() => setRenewing(null)} />}
  </Card>;
}

/** Modal por miembro del equipo: documentos del operador (visa, licencia, I-94,
    apto médico…) y sus unidades habituales (setMemberUnits) que autollenan el
    viaje al asignárselo. Unidad en taller o fuera de servicio no es elegible. */
export function MemberDocsModal({ member, units, docs, onClose }: {
  member: Session; units: Unit[]; docs: UnitDocument[]; onClose(): void;
}) {
  const { token, busy, mutate, data } = usePortal();
  const company = data?.company ?? null;
  const [adding, setAdding] = useState(false);
  const memberDocs = docs.filter(d => d.subjectType === 'member' && d.subjectId === member.uid);
  // Unidad habitual = vehículo motriz + remolque remolcado, por categoría.
  const tractors = units.filter(u => unitCategoryOf(u, company) === 'vehicle' && u.status !== 'sold');
  const trailers = units.filter(u => unitCategoryOf(u, company) === 'attachment' && u.status !== 'sold');

  const saveUnits = (tractor: string, trailer: string) => {
    void mutate(() => api('setMemberUnits', {
      uid: member.uid,
      tractorUnitId: tractor || null,
      trailerUnitId: trailer || null,
    }, token), 'Unidad habitual actualizada.');
  };

  // Unidad no activa: la opción queda a la vista (si ya era la habitual se ve
  // seleccionada con su estado) pero no se puede volver a elegir.
  const unitOption = (u: Unit) =>
    <option key={u.id} value={u.id} disabled={u.status !== 'active'}>
      {unitName(u, company)}{u.status !== 'active' ? ` · ${UNIT_STATUS[u.status]?.label ?? u.status}` : ''}
    </option>;

  return <Modal title={member.displayName || 'Operador'} onClose={onClose} wide>
    <div>
      <h3 className="card-title sm">Unidad habitual</h3>
      <p className="card-detail u-m0">Autollenan el vehículo y el remolque al asignarle un viaje; puedes rotarlos aquí.</p>
      <div className="form-grid u-mt">
        <Select label="Vehículo habitual" value={member.defaultTractorId ?? ''} disabled={busy}
          onChange={e => saveUnits(e.target.value, member.defaultTrailerId ?? '')}>
          <option value="">Sin vehículo habitual</option>
          {tractors.map(unitOption)}
        </Select>
        <Select label="Remolque habitual" value={member.defaultTrailerId ?? ''} disabled={busy}
          onChange={e => saveUnits(member.defaultTractorId ?? '', e.target.value)}>
          <option value="">Sin remolque habitual</option>
          {trailers.map(unitOption)}
        </Select>
      </div>
    </div>

    <div className="row between multiline">
      <h3 className="card-title sm">Documentos del operador</h3>
      <Btn small onClick={() => setAdding(true)}>Agregar documento</Btn>
    </div>
    <DocList docs={memberDocs} />

    {adding && <DocumentForm subjectType="member" subjectId={member.uid} onClose={() => setAdding(false)} />}
  </Modal>;
}

// ---- utilidades del módulo ----

/** El input date da YYYY-MM-DD; lo anclamos al mediodía local para que el día
    no se mueva al convertir a ISO en cualquier huso de MX/US. */
export function dateInputToIso(value: string): string | null {
  if (!value) return null;
  const t = Date.parse(`${value}T12:00:00`);
  return Number.isFinite(t) ? new Date(t).toISOString() : null;
}

export function isoToDateInput(iso: string | null | undefined): string {
  const t = iso ? Date.parse(iso) : NaN;
  if (!Number.isFinite(t)) return '';
  const d = new Date(t);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function fileToBase64(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? '').split(',')[1] ?? '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/** Foto lista para la API (límite 4.5 MB del servicio): si pesa más de ~4 MB
    o es de un tipo que no acepta, la reduce a JPEG de máx 1920 px por el lado
    largo — las fotos de celular suelen pesar 3–8 MB. Si el navegador no puede
    decodificarla (HEIC en Chrome), se manda tal cual y el servidor valida. */
export async function photoFromFile(file: File): Promise<{ contentType: string; dataBase64: string }> {
  const okType = /^image\/(jpeg|png|webp|heic)$/.test(file.type);
  if (file.size <= 4_000_000 && okType) return { contentType: file.type, dataBase64: await fileToBase64(file) };
  try {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, 1920 / Math.max(bmp.width, bmp.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bmp.width * scale));
    canvas.height = Math.max(1, Math.round(bmp.height * scale));
    canvas.getContext('2d')?.drawImage(bmp, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.85));
    if (blob) return { contentType: 'image/jpeg', dataBase64: await fileToBase64(blob) };
  } catch { /* formato sin decodificador en este navegador */ }
  return { contentType: okType ? file.type : 'image/jpeg', dataBase64: await fileToBase64(file) };
}

/** Adjunto de documento listo para `files` del input (máx 4, ≤4.5 MB c/u):
    las imágenes pasan por photoFromFile (compresión JPEG si pesan) y el PDF
    viaja tal cual en base64 — el servicio solo acepta image/* y
    application/pdf. */
export async function fileToAttachment(file: File): Promise<{ name: string; contentType: string; dataBase64: string }> {
  if (file.type.startsWith('image/')) {
    const p = await photoFromFile(file);
    return { name: file.name, contentType: p.contentType, dataBase64: p.dataBase64 };
  }
  return { name: file.name, contentType: 'application/pdf', dataBase64: await fileToBase64(file) };
}
